import { Inject, Injectable } from '@angular/core';
import { AppPartnerService, AppPricingService } from '@galaxy/marketplace-apps';
import { App, AppKey, FieldMask } from '@vendasta/marketplace-apps';
import { Package, PackageService } from '@vendasta/marketplace-packages';
import { LineItem } from '@vendasta/sales-orders';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { MARKET_ID, PARTNER_ID } from '../../shared/constants';
import {
  AppVariablePriceMap,
  PackageVariablePriceMap,
  VariablePriceInputs,
} from '../variable-prices/variable-prices.component';

@Injectable({
  providedIn: 'root',
})
export class VariablePricesService {
  private lineItems$$ = new BehaviorSubject<LineItem[]>([]);
  variablePricesMap$: Observable<AppVariablePriceMap>;
  variablePackagePricesMap$: Observable<PackageVariablePriceMap>;

  constructor(
    @Inject(PARTNER_ID) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID) private readonly marketId$: Observable<string>,
    private readonly appSdk: AppPartnerService,
    private readonly packageSdk: PackageService,
    private readonly appPricingSdk: AppPricingService,
  ) {
    this.initializeService();
  }

  setLineItems(lineItems: LineItem[]): void {
    this.lineItems$$.next(lineItems ?? []);
  }

  private initializeService(): void {
    const lineItemAppIds$ = this.lineItems$$.pipe(
      map((lineItems) => {
        return lineItems.filter((li) => li.appKey?.appId).map((li) => li.appKey.appId);
      }),
    );
    const lineItemPackageIds$ = this.lineItems$$.pipe(
      map((lineItems) => {
        return lineItems.filter((li) => li.packageId).map((li) => li.packageId);
      }),
    );

    const packages$ = lineItemPackageIds$.pipe(
      switchMap((packageIds) => {
        if (!packageIds.length) {
          return of([]);
        }
        return this.packageSdk.getMulti(packageIds);
      }),
      shareReplay(1),
    );
    const appIdsFromPackages$ = packages$.pipe(
      map((packages) => {
        return getAppIdsFromPackages(packages);
      }),
    );
    const allApps$ = combineLatest([lineItemAppIds$, appIdsFromPackages$]).pipe(
      withLatestFrom(this.partnerId$, this.marketId$),
      switchMap(([[appIds, appIdsFromPackages], partnerId, marketId]) => {
        const allAppIDs = Array.from(new Set(appIds.concat(appIdsFromPackages)));
        const appKeys: AppKey[] = convertAppIdsIntoAppKeys(allAppIDs);
        if (!appKeys.length) {
          return of([]);
        }
        return this.appSdk.getMulti(appKeys, partnerId, marketId, { paths: ['key', 'sharedMarketingInformation'] });
      }),
      shareReplay(1),
    );
    const pricesForAllApps$ = allApps$.pipe(
      withLatestFrom(this.partnerId$, this.marketId$),
      switchMap(([allApps, partnerId, marketId]) => {
        const appIds = allApps.map((app) => app.key.appId);
        if (!appIds.length) {
          return of([]);
        }
        return this.appPricingSdk.getMultiPricing(appIds, new FieldMask({ paths: ['wholesale'] }), partnerId, marketId);
      }),
      shareReplay(1),
    );

    const variablePriceInputs$ = pricesForAllApps$.pipe(
      withLatestFrom(this.lineItems$$, allApps$, packages$),
      map(([appPrices, lineItems, allApps, packages]) => {
        return appPrices
          .filter((appPrice) => appPrice.pricesForContexts['wholesale'].billingConfiguration?.usesVariablePricing)
          .map((appPrice) => {
            let lineItem = findMatchingLineItem(lineItems, appPrice.appId);
            if (lineItem) {
              const app = findMatchingApp(lineItem.appKey.appId, allApps);
              return {
                appPrice: appPrice.pricesForContexts['wholesale'],
                name: app.sharedMarketingInformation.name,
                iconUrl: app.sharedMarketingInformation.iconUrl,
                customPrice: lineItem.cost?.customPrice ?? undefined,
              } as VariablePriceInputs;
            }

            const app = findMatchingApp(appPrice.appId, allApps);
            const pkg = findMatchingPackage(appPrice.appId, packages);
            lineItem = findMatchingLineItem(lineItems, pkg.packageId);
            const customPrice = lineItem?.customPrices?.find((cp) => cp.productId === appPrice.appId)?.customPrice;
            return {
              appPrice: appPrice.pricesForContexts['wholesale'],
              name: app.sharedMarketingInformation.name,
              iconUrl: app.sharedMarketingInformation.iconUrl,
              packageID: pkg.packageId,
              packageName: pkg.name,
              packageIconUrl: pkg.icon,
              customPrice: customPrice ?? undefined,
            } as VariablePriceInputs;
          });
      }),
      shareReplay(1),
    );
    this.variablePricesMap$ = variablePriceInputs$.pipe(
      map((variablePrices) => {
        const variablePricesMap = new Map();
        variablePrices
          .filter((vp) => !vp.packageID)
          .forEach((variablePrice: VariablePriceInputs) => {
            variablePricesMap.set(variablePrice.appPrice.appId, variablePrice);
          });
        return variablePricesMap;
      }),
    );
    this.variablePackagePricesMap$ = variablePriceInputs$.pipe(
      map((variablePrices) => {
        const variablePackagePricesMap = new Map();
        variablePrices
          .filter((vp) => vp.packageID)
          .forEach((variablePrice: VariablePriceInputs) => {
            let variablePriceMap = variablePackagePricesMap.get(variablePrice.packageID);
            if (!variablePriceMap) {
              variablePriceMap = new Map();
              variablePackagePricesMap.set(variablePrice.packageID, variablePriceMap);
            }
            variablePriceMap.set(variablePrice.appPrice.appId, variablePrice);
          });
        return variablePackagePricesMap;
      }),
    );
  }
}

function getAppIdsFromPackages(packages: Package[]): string[] {
  const appIDsFromPackages: string[] = [];
  packages.forEach((p) => {
    p.lineItems.lineItems.forEach((li) => {
      appIDsFromPackages.push(li.id);
    });
  });
  return appIDsFromPackages;
}

function convertAppIdsIntoAppKeys(appIds: string[]): AppKey[] {
  return appIds.map((ai) => {
    return new AppKey({
      appId: ai,
    });
  });
}

function findMatchingLineItem(lineItems: LineItem[], itemId: string): LineItem {
  return lineItems.find((li) => {
    if (li.appKey?.appId) {
      return li.appKey.appId === itemId;
    }
    return li.packageId === itemId;
  });
}

function findMatchingApp(appId: string, apps: App[]): App {
  return apps.find((a) => a.key.appId === appId);
}

function findMatchingPackage(appId: string, packages: Package[]): Package {
  return packages.find((p) => {
    return p.lineItems.lineItems.find((li) => {
      return li.id === appId;
    });
  });
}
